export interface MenuOptions {
    root: HTMLElement;
    breakpoint: number;
    openDelay?: number;
    closeDelay?: number;
    resizeDebounce?: number;
}

export class Menu {
    private readonly root: HTMLElement;
    private readonly breakpoint: number;

    private readonly openDelay: number;
    private readonly closeDelay: number;
    private readonly resizeDebounce: number;

    private readonly toggleButton: HTMLButtonElement;
    private readonly closeButton: HTMLButtonElement;
    private readonly sideMenu: HTMLElement;
    private readonly overlay: HTMLElement;
    private readonly menuLinks: HTMLAnchorElement[];

    private openTimer?: ReturnType<typeof setTimeout>;
    private closeTimer?: ReturnType<typeof setTimeout>;
    private resizeTimer?: ReturnType<typeof setTimeout>;

    private opened = false;

    public constructor(options: MenuOptions) {
        this.root = options.root;
        this.breakpoint = options.breakpoint;

        this.openDelay = options.openDelay ?? 0;
        this.closeDelay = options.closeDelay ?? 0;
        this.resizeDebounce = options.resizeDebounce ?? 150;

        this.toggleButton = this.getRequiredElement<HTMLButtonElement>('[data-menu9-toggle]');

        this.closeButton = this.getRequiredElement<HTMLButtonElement>('[data-menu9-close]');

        this.sideMenu = this.getRequiredElement<HTMLElement>('[data-menu9-side]');

        this.overlay = this.getRequiredElement<HTMLElement>('[data-menu9-overlay]');

        this.menuLinks = Array.from(this.root.querySelectorAll<HTMLAnchorElement>('[data-menu9-list] a'));

        this.bindEvents();
        this.synchronizeWithViewport();
        this.updateState(false);
    }

    public open(): void {
        if (window.innerWidth >= this.breakpoint || this.opened) {
            return;
        }

        this.clearTimers();

        this.openTimer = setTimeout(() => {
            this.updateState(true);
            // preventScroll: moving focus must not scroll the page (see close()).
            // A site that hides the close button (the toggle turns into X) gets the first link.
            const target = this.closeButton.getClientRects().length ? this.closeButton : this.menuLinks[0];
            target?.focus({ preventScroll: true });
        }, this.openDelay);
    }

    public close(restoreFocus = false): void {
        // Przed wcześniejszym wyjściem: przy openDelay menu jeszcze nie jest
        // otwarte, ale czekające otwarcie nie może się wykonać po zamknięciu.
        this.clearTimers();

        if (!this.opened && !this.root.classList.contains('menu9-is-open')) {
            return;
        }

        this.closeTimer = setTimeout(() => {
            this.updateState(false);

            if (restoreFocus) {
                // The toggle sits in a sticky header; a plain focus() made
                // the browser "scroll it into view" from its place in the
                // document flow, so closing the menu mid-page jumped the
                // page by several hundred pixels on phones.
                this.toggleButton.focus({ preventScroll: true });
            }
        }, this.closeDelay);
    }

    public toggle(): void {
        if (this.opened) {
            this.close(true);
            return;
        }

        this.open();
    }

    private bindEvents(): void {
        this.toggleButton.addEventListener('click', () => {
            this.toggle();
        });

        this.closeButton.addEventListener('click', () => {
            this.close(true);
        });

        this.overlay.addEventListener('click', () => {
            this.close(true);
        });

        this.menuLinks.forEach((link) => {
            link.addEventListener('click', () => {
                this.close();
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.opened) {
                this.close(true);
            }
        });

        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);

            this.resizeTimer = setTimeout(() => {
                this.synchronizeWithViewport();
            }, this.resizeDebounce);
        });
    }

    private synchronizeWithViewport(): void {
        const desktopMode = window.innerWidth >= this.breakpoint;

        this.root.classList.toggle('menu9-is-desktop', desktopMode);
        this.root.classList.toggle('menu9-is-mobile', !desktopMode);

        if (desktopMode) {
            this.updateState(false);
        }
    }

    private updateState(opened: boolean): void {
        this.opened = opened;

        this.root.classList.toggle('menu9-is-open', opened);
        document.body.classList.toggle('menu9-lock-scroll', opened);

        this.toggleButton.setAttribute('aria-expanded', String(opened));

        this.sideMenu.setAttribute('aria-hidden', String(!opened));
        this.sideMenu.inert = !opened;

        this.overlay.setAttribute('aria-hidden', String(!opened));
        this.overlay.inert = !opened;

        // Panel links are in the tab order only while the panel is open (tabindex="-1" in menu.ejs).
        this.menuLinks.forEach((link) => link.setAttribute('tabindex', opened ? '0' : '-1'));
    }

    private clearTimers(): void {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
    }

    private getRequiredElement<T extends Element>(selector: string): T {
        const element = this.root.querySelector<T>(selector);

        if (!element) {
            throw new Error(`Menu9: nie znaleziono elementu "${selector}".`);
        }

        return element;
    }
}
